import { useEffect, useState } from 'react';
import { useInvoiceSettings, useSaveInvoiceSettings } from '@/hooks/useSettings';
import { Screen } from '@m/ui/Screen';
import { Group } from '@m/ui/List';
import { BarButton, SkeletonList, Switch } from '@m/ui/Controls';
import { FieldRow } from '../clients/forms';

/** The invoice defaults that matter on a phone. Templates and email copy stay on the desktop. */
export function InvoiceSettingsScreen() {
  const settings = useInvoiceSettings();
  const save = useSaveInvoiceSettings();
  const s = settings.data;
  const [f, setF] = useState({ rate: '', terms: '', prefix: '', next: '', notes: '', autoSent: false });

  useEffect(() => {
    if (s)
      setF({
        rate: String(s.defaultHourlyRate),
        terms: String(s.paymentTermsDays),
        prefix: s.invoiceNumberPrefix,
        next: String(s.nextInvoiceNumber),
        notes: s.defaultNotes ?? '',
        autoSent: s.autoMarkSentOnManualSend
      });
  }, [s]);

  const dirty =
    !!s &&
    (f.rate !== String(s.defaultHourlyRate) ||
      f.terms !== String(s.paymentTermsDays) ||
      f.prefix !== s.invoiceNumberPrefix ||
      f.next !== String(s.nextInvoiceNumber) ||
      f.notes !== (s.defaultNotes ?? '') ||
      f.autoSent !== s.autoMarkSentOnManualSend);
  const valid = Number(f.rate) >= 0 && Number.isInteger(Number(f.terms)) && Number(f.terms) >= 0 && Number.isInteger(Number(f.next)) && Number(f.next) > 0;

  const set = (k: 'rate' | 'terms' | 'prefix' | 'next' | 'notes') => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  return (
    <Screen
      title="Invoice settings"
      back="/more"
      backLabel="More"
      large={false}
      actions={
        <BarButton
          className="font-semibold"
          disabled={!dirty || !valid || save.isPending}
          onClick={() =>
            save.mutate({
              defaultHourlyRate: Number(f.rate),
              paymentTermsDays: Number(f.terms),
              invoiceNumberPrefix: f.prefix,
              nextInvoiceNumber: Number(f.next),
              defaultNotes: f.notes || null,
              autoMarkSentOnManualSend: f.autoSent
            })
          }
        >
          Save
        </BarButton>
      }
    >
      {!s ? (
        <SkeletonList rows={4} />
      ) : (
        <>
          <Group header="Defaults" footer="Used for any client without its own rate.">
            <FieldRow label="Hourly rate" inputMode="decimal" value={f.rate} onChange={set('rate')} />
            <FieldRow label="Payment terms (days)" inputMode="numeric" value={f.terms} onChange={set('terms')} />
          </Group>
          <Group header="Numbering">
            <FieldRow label="Prefix" autoCapitalize="characters" value={f.prefix} onChange={set('prefix')} />
            <FieldRow label="Next number" inputMode="numeric" value={f.next} onChange={set('next')} />
          </Group>
          <Group header="Notes on every invoice">
            <FieldRow label="Notes" placeholder="None" value={f.notes} onChange={set('notes')} />
          </Group>
          <Group footer="When you send from your own mail app, mark the invoice sent without asking.">
            <label className="flex min-h-[52px] items-center gap-3 px-4">
              <span className="flex-1">Mark sent automatically</span>
              <Switch checked={f.autoSent} onChange={(v) => setF({ ...f, autoSent: v })} label="Mark sent automatically" />
            </label>
          </Group>
        </>
      )}
    </Screen>
  );
}
